import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Edit,
  X,
  Save,
  Mail,
  Phone,
  MapPin,
  Building2,
  Calendar,
  StickyNote,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/ActiveClinicContext';
import {
  useSubClinic,
  useUpdateSubClinic,
  useToggleSubClinicStatus,
  useDeleteSubClinic,
} from '@/hooks/useMultiClinic';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import type { Json } from '@/types/database.generated';

interface AddressShape {
  street: string;
  zip: string;
  city: string;
  country: string;
}

const EMPTY_ADDRESS: AddressShape = {
  street: '',
  zip: '',
  city: '',
  country: 'Danmark',
};

function readAddress(value: Json | null): AddressShape {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return EMPTY_ADDRESS;
  }
  const v = value as Record<string, Json>;
  return {
    street: typeof v.street === 'string' ? v.street : '',
    zip: typeof v.zip === 'string' ? v.zip : typeof v.zip_code === 'string' ? v.zip_code : '',
    city: typeof v.city === 'string' ? v.city : '',
    country: typeof v.country === 'string' ? v.country : 'Danmark',
  };
}

function formatAddress(addr: AddressShape): Json {
  return {
    street: addr.street,
    zip: addr.zip,
    city: addr.city,
    country: addr.country,
  };
}

export function SubClinicDetail() {
  const { t } = useTranslation();
  const { clinicId } = useParams<{ clinicId: string }>();
  const navigate = useNavigate();
  const { isMainClinicAdmin } = useActiveClinic();
  const { data: clinic, isLoading, error } = useSubClinic(clinicId);
  const update = useUpdateSubClinic();
  const toggleStatus = useToggleSubClinicStatus();
  const deleteClinic = useDeleteSubClinic();

  const [editing, setEditing] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cvr, setCvr] = useState('');
  const [address, setAddress] = useState<AddressShape>(EMPTY_ADDRESS);

  useEffect(() => {
    if (!clinic) return;
    setName(clinic.name);
    setEmail(clinic.email ?? '');
    setPhone(clinic.phone ?? '');
    setCvr(clinic.cvr ?? '');
    setAddress(readAddress(clinic.address));
  }, [clinic]);

  // Redirect if not main clinic admin
  if (!isMainClinicAdmin && !isLoading) {
    return (
      <div className="min-h-screen bg-page-light p-6 dark:bg-page-dark">
        <ErrorAlert message={t('settings.clinics.unauthorized')} />
        <Button variant="secondary" onClick={() => navigate('/settings/clinics')}>
          {t('common.back')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-light dark:bg-page-dark">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className="min-h-screen bg-page-light p-6 dark:bg-page-dark">
        <ErrorAlert message={t('settings.clinics.error_not_found')} />
        <Button variant="secondary" onClick={() => navigate('/settings/clinics')}>
          {t('settings.clinics.back_to_list')}
        </Button>
      </div>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!clinicId) return;

    try {
      await update.mutateAsync({
        clinicId,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        cvr: cvr.trim() || null,
        address: formatAddress(address),
      });
      toast.success(t('settings.clinics.updated_successfully'));
      setEditing(false);
    } catch (err) {
      console.error('Failed to update clinic:', err);
      setSubmitError(t('settings.clinics.update_error'));
    }
  };

  const onToggleStatus = async () => {
    if (!clinicId) return;

    const isActive = (clinic as { is_active?: boolean }).is_active ?? true;
    const willActivate = !isActive;
    if (
      !window.confirm(
        willActivate
          ? t('settings.clinics.confirm_activate')
          : t('settings.clinics.confirm_deactivate')
      )
    ) {
      return;
    }

    try {
      await toggleStatus.mutateAsync({ clinicId, active: willActivate });
      toast.success(
        willActivate
          ? t('settings.clinics.activated_successfully')
          : t('settings.clinics.deactivated_successfully')
      );
    } catch (err) {
      console.error('Failed to toggle status:', err);
      toast.error(t('settings.clinics.status_toggle_error'));
    }
  };

  const onDelete = async () => {
    if (!clinicId) return;

    try {
      await deleteClinic.mutateAsync(clinicId);
      toast.success(t('settings.clinics.deleted_successfully'));
      navigate('/settings/clinics');
    } catch (err) {
      console.error('Failed to delete clinic:', err);
      toast.error(t('settings.clinics.delete_error'));
      setShowDeleteModal(false);
    }
  };

  const onCancelEdit = () => {
    setEditing(false);
    setSubmitError(null);
    // Reset form to current clinic data
    if (clinic) {
      setName(clinic.name);
      setEmail(clinic.email ?? '');
      setPhone(clinic.phone ?? '');
      setCvr(clinic.cvr ?? '');
      setAddress(readAddress(clinic.address));
    }
  };

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="mx-auto max-w-5xl px-6">
          <button
            type="button"
            onClick={() => navigate('/settings/clinics')}
            className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('settings.clinics.back_to_list')}
          </button>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl">
                  {clinic.name}
                </h2>
                {!(clinic as { is_active?: boolean }).is_active && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                    {t('settings.clinics.inactive')}
                  </span>
                )}
                {(clinic as { is_active?: boolean }).is_active && (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-200">
                    {t('settings.clinics.active')}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('settings.clinics.created_at')}{' '}
                {new Date(clinic.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {!editing && (
                <>
                  <Button
                    variant="secondary"
                    onClick={onToggleStatus}
                    disabled={toggleStatus.isPending}
                  >
                    {toggleStatus.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (clinic as { is_active?: boolean }).is_active ? (
                      t('settings.clinics.deactivate')
                    ) : (
                      t('settings.clinics.activate')
                    )}
                  </Button>
                  <Button onClick={() => setEditing(true)}>
                    <Edit className="mr-2 h-4 w-4" />
                    {t('common.edit')}
                  </Button>
                </>
              )}
              {editing && (
                <Button
                  variant="secondary"
                  onClick={onCancelEdit}
                  disabled={update.isPending}
                >
                  <X className="mr-2 h-4 w-4" />
                  {t('common.cancel')}
                </Button>
              )}
            </div>
          </div>

          {submitError && (
            <div className="mt-6">
              <ErrorAlert message={submitError} />
            </div>
          )}

          {editing ? (
            <form
              onSubmit={onSubmit}
              className="mt-6 space-y-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark"
            >
              <Section title={t('settings.clinics.general_info')}>
                <Grid>
                  <Input
                    label={t('settings.clinics.name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                  <Input
                    label={t('settings.clinics.email')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <Input
                    label={t('settings.clinics.phone')}
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <Input
                    label={t('settings.clinics.cvr')}
                    value={cvr}
                    onChange={(e) => setCvr(e.target.value)}
                  />
                </Grid>
              </Section>

              <Section title={t('settings.clinics.address')}>
                <Grid>
                  <div className="sm:col-span-2">
                    <Input
                      label={t('settings.clinics.street')}
                      value={address.street}
                      onChange={(e) => setAddress({ ...address, street: e.target.value })}
                    />
                  </div>
                  <Input
                    label={t('settings.clinics.zip')}
                    value={address.zip}
                    onChange={(e) => setAddress({ ...address, zip: e.target.value })}
                  />
                  <Input
                    label={t('settings.clinics.city')}
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                  />
                  <div className="sm:col-span-2">
                    <Input
                      label={t('settings.clinics.country')}
                      value={address.country}
                      onChange={(e) => setAddress({ ...address, country: e.target.value })}
                    />
                  </div>
                </Grid>
              </Section>

              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                <Button type="submit" disabled={update.isPending || !name.trim() || !email.trim()}>
                  {update.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {t('common.save')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <Card title={t('settings.clinics.general_info')}>
                  <Field
                    icon={<Building2 className="h-4 w-4 text-gray-400" />}
                    label={t('settings.clinics.name')}
                    value={clinic.name}
                  />
                  <Field
                    icon={<Mail className="h-4 w-4 text-gray-400" />}
                    label={t('settings.clinics.email')}
                    value={clinic.email ?? '—'}
                  />
                  <Field
                    icon={<Phone className="h-4 w-4 text-gray-400" />}
                    label={t('settings.clinics.phone')}
                    value={clinic.phone ?? '—'}
                  />
                  <Field
                    label={t('settings.clinics.cvr')}
                    value={clinic.cvr ?? '—'}
                  />
                </Card>

                <Card title={t('settings.clinics.address')}>
                  <Field
                    icon={<MapPin className="h-4 w-4 text-gray-400" />}
                    label={t('settings.clinics.street')}
                    value={readAddress(clinic.address).street || '—'}
                  />
                  <Field
                    label={`${t('settings.clinics.zip')} / ${t('settings.clinics.city')}`}
                    value={
                      readAddress(clinic.address).zip || readAddress(clinic.address).city
                        ? `${readAddress(clinic.address).zip} ${readAddress(clinic.address).city}`.trim()
                        : '—'
                    }
                  />
                  <Field
                    label={t('settings.clinics.country')}
                    value={readAddress(clinic.address).country || '—'}
                  />
                </Card>
              </div>

              <div className="space-y-6">
                <Card title={t('settings.clinics.statistics')}>
                  <Field
                    label={t('settings.clinics.patient_count')}
                    value={(clinic.patient_counter ?? 0).toString()}
                  />
                </Card>

                <Card title={t('settings.clinics.metadata')}>
                  <Field
                    icon={<Calendar className="h-4 w-4 text-gray-400" />}
                    label={t('settings.clinics.created_at')}
                    value={new Date(clinic.created_at).toLocaleString()}
                  />
                  <Field
                    icon={<StickyNote className="h-4 w-4 text-gray-400" />}
                    label={t('settings.clinics.updated_at')}
                    value={new Date(clinic.updated_at).toLocaleString()}
                  />
                </Card>

                <Card title={t('settings.clinics.danger_zone')}>
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('settings.clinics.delete_warning')}
                    </p>
                    <Button
                      variant="secondary"
                      onClick={() => setShowDeleteModal(true)}
                      className="w-full border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('settings.clinics.delete_clinic')}
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
              <AlertCircle className="h-6 w-6" />
              <h3 className="text-lg font-semibold">
                {t('settings.clinics.confirm_delete_title')}
              </h3>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
              {t('settings.clinics.confirm_delete_message', { name: clinic.name })}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteClinic.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={onDelete}
                disabled={deleteClinic.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteClinic.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('settings.clinics.delete_clinic')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-card-dark">
      <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function Field({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
        {icon}
        {label}
      </span>
      <span className="text-right text-gray-900 dark:text-white">{value}</span>
    </div>
  );
}
